import ContactGroupItem     from '../Entities/ContactGroupItem';
import ContactItem          from '../Entities/ContactItem';
import ContactPersonItem    from '../Entities/ContactPersonItem';
import CollectionList       from '@/js/Shared/Collections/CollectionList';
import SimpleCollectionList from '@/js/Shared/Collections/SimpleCollectionList';

export { ContactsSelectionCollection as default };

/**
* Contacts Collection List
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ContactsSelectionCollection extends CollectionList {
  /**
   * Defines the number os selected groups
   *
   * @public
   *
   * @type {Number}
   *
   * @since 7.0.0
   */
  groups_count = 0;

  /**
   * Selected emails Collection
   *
   * Holds all contacts emails from the entire collection
   *
   * @public
   *
   * @type {SimpleCollectionList}
   *
   * @since 7.0.0
   */
  emails_selected = new SimpleCollectionList();

  /**
   * Constructor
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  constructor() {
    super('id');
  }

  /**
   * Adds a contact to collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactItem} contact c
   *
   * @return {Boolean}
   */
  add(contact) {
    if (super.add(contact) === false) {
      return false;
    }

    if (contact.type === 'group') {
      this.addGroupSelectedEmails(contact);
      this.groups_count++;
      return true;
    }

    this.addPersonSelectedEmails(contact);
    return true;
  }

  /**
   * Deletes a contact from the collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} contact_id
   *
   * @return {Boolean}
   */
  delete(contact_id) {
    if (super.delete(contact_id) === false) {
      return;
    }

    if (this.last_deleted.type === 'group') {
      this.groups_count--;
      this.deleteGroupSelectedEmails(this.last_deleted);
      return true;
    }

    this.deletePersonSelectedEmails(this.last_deleted);
    return true;
  }

  /**
   * Delete a contact list ids
   *
   * @param {Array} contact_ids
   * @return {void}
   */
  deleteContactsFromArray(contact_ids){
    if(contact_ids.length === 0){
      return;
    }

    contact_ids.forEach(id => this.delete(id));
  }

  /**
   * Browses all group members to delete all persons with emails
   * from the selection email collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactGroupItem} group
   *
   * @returns {void}
   */
  deleteGroupSelectedEmails(group) {
    for (let k in group.members) {
      this.deletePersonSelectedEmails(group.members[k]);
    }
  }

  /**
   * Deletes a person email from the selected emails list
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactPersonItem} contact
   *
   * @returns {void}
   */
  deletePersonSelectedEmails(contact) {
    if(!contact.email){
      return;
    }

    this.emails_selected.delete(contact.email);
  }

  /**
   * Browses to group members to add all persons with emails
   * to the selection email collection
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {ContactPersonItem} contact
   *
   * @returns {void}
   */
  addGroupSelectedEmails(group) {
    for (let k in group.members) {
      if (group.members[k].email) {
        this.addPersonSelectedEmails(group.members[k]);
      }
    }
  }

  /**
   * Adds a person email to the selected emails list
   * when person has an email set
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {ContactPersonItem} contact
   *
   * @returns {void}
   */
  addPersonSelectedEmails(contact) {
    if(!contact.email){
      return;
    }

    this.emails_selected.add(contact.email, contact.email);
  }

  /**
   * Get all contacts that have email
   *
   * @returns {Array}
   */
  getRecipients() {
    const recipients = [];

    this.items.forEach(contact => {
      if (contact.type === 'person' && contact.email) {
        this.addPersonRecipients(contact, recipients);
      }

      if (contact.type === 'group') {
        this.mapGroupRecipients(contact, recipients);
      }
    });

    return recipients;
  }

  /**
   * Adds a contact to the list of recipients
   *
   * @param {Object.<ContactPersonItem>} contact
   * @param {Array} recipients
   */
  addPersonRecipients(contact, recipients) {
    recipients.push(contact);
  }

  /**
   * Loop through a group's contact list by checking
   * if you have an email and add it to the list of recipients
   *
   * @param {Object.<ContactPersonItem>} contact
   * @param {Array} recipients
   */
  mapGroupRecipients(contact, recipients) {
    for (const index in contact.members) {
      const member = contact.members[index];

      if(member.email){
        this.addPersonRecipients(member, recipients);
      }
    }
  }

  /**
   * Resets the collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  reset() {
    this.groups_count = 0;
    this.emails_selected.reset();
    super.reset();
  }
}